<template>
  <div>
    <b-row class="text-uppercase mb-3">

      <b-col md="6" class="mb-2">
        <h5 class="text-primary font-weight-bold mb-3">
          {{$t($getConst.pages.invoice_details.invoice_vendor_name)}}:
          <b-skeleton width="7rem" class="float-right d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="text-secondary">{{invoice.invoice_payments[0].timesheet.sub_contractor_name}}</span>
        </h5>
        <h6 class="mb-3 text-muted font-weight-bold">
          {{$t($getConst.pages.invoice_details.invoice_vendor_id)}}:
          <b-skeleton width="6rem" class="float-right d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">{{invoice.invoice_payments[0].timesheet.vendor_gp_id}}</span>
        </h6>
        <h6 class="mb-3 text-muted font-weight-bold">
          {{$t($getConst.pages.invoice_details.invoice_assignment_id)}}:
          <b-skeleton width="5rem" class="float-right d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">{{invoice.invoice_payments[0].timesheet.assignment_id}}</span>
        </h6>
        <h6 class="mb-3 text-muted font-weight-bold">
          {{$t($getConst.pages.vendor_timesheet_details.vendor_timesheet_details_contractor_name)}}:
          <b-skeleton width="6rem" class="float-right d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">{{invoice.invoice_payments[0].timesheet.contractor_name}}</span>
        </h6>
        <h6 class="mb-3 text-muted font-weight-bold">
          {{$t($getConst.pages.invoice_details.assignment_start_date)}}:
          <b-skeleton width="5rem" class="float-right d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">{{formatDate(invoice.assignment_start_date)}}</span>
        </h6>
        <h6 class="mb-3 text-muted font-weight-bold">
          {{$t($getConst.pages.invoice_details.payment_status)}}:
          <b-skeleton width="5rem" class="float-right d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">{{invoice.payment_status}}</span>
        </h6>
      </b-col>

      <b-col md="6">
        <h5 class="text-primary font-weight-bold mb-3">
          {{$t($getConst.pages.invoice_details.statement_date)}}:
          <b-skeleton width="8rem" class="float-right d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="text-secondary">{{formatDate(invoice.statement_date)}}</span>
        </h5>
        <h6 class="mb-3 text-muted font-weight-bold">
          {{$t($getConst.pages.invoice_details.invoice_id)}}:
          <b-skeleton width="5rem" class="float-right d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">{{invoice.invoice_id}}</span>
        </h6>
        <h6 class="mb-3 text-muted font-weight-bold">
          {{$t($getConst.pages.invoice_details.billing_term_category)}}:
          <b-skeleton width="5rem" class="float-right d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">{{invoice.billing_term_category}}</span>
        </h6>
        <h6 class="mb-3 text-muted font-weight-bold">
          {{$t($getConst.pages.invoice_details.billing_period)}}:
          <b-skeleton width="5rem" class="float-right d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">{{invoice.billing_period}}</span>
        </h6>
        <h6 class="mb-3 text-muted font-weight-bold">
          {{$t($getConst.pages.invoice_details.currency)}}:
          <b-skeleton width="5rem" class="float-right d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">{{invoice.currency}}</span>
        </h6>
        <h6 class="mb-3 text-muted font-weight-bold">
          {{$t($getConst.pages.invoice_details.assignment_end_date)}}:
          <b-skeleton width="5rem" class="float-right d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">{{formatDate(invoice.assignment_end_date)}}</span>
        </h6>
      </b-col>

    </b-row>

    <AppSection
      v-if="isLoading || (invoice && invoice.invoice_payments.length)"
      id="invoices-timesheet"
      type="section"
      :title="$getConst.pages.invoice_details.timesheets"
    >
      <b-row class="text-capitalize">
        <template v-if="isLoading">
          <b-col lg="4" md="6" sm="12">
            <b-skeleton-img height="10rem"/>
          </b-col>
          <b-col lg="4" md="6" sm="12">
            <b-skeleton-img height="10rem"/>
          </b-col>
        </template>
        <b-col v-else class="mb-3" lg="4" md="6" sm="12" v-for="(invoice_payments) in invoice.invoice_payments">
          <b-card>
            <h5 class="text-primary font-weight-bold mb-3">
              {{invoice_payments.timesheet.timesheet_id}}
              <span v-if="invoice_payments.timesheet.timesheet_status">
                <a @click="$router.push({name: $getConst.routesNames.vendorTimesheetDetails, params: {timesheetId: invoice_payments.timesheet.id}})">
                  <i style="cursor: pointer;" :class="$getConst.icons.searchPlus"/>
                </a>
              </span>
            </h5>
            <h6 class="text-muted font-weight-bold">
              {{$t($getConst.pages.vendor_timesheet_details.vendor_timesheet_details_week_end_date)}}:
              <span class="font-weight-light">{{formatDate(invoice_payments.timesheet.timesheet_week_end_date)}}</span>
            </h6>
            <h6 class="text-muted font-weight-bold">
              {{$t($getConst.pages.invoice_details.units)}}:
              <span class="font-weight-light">{{invoice_payments.units | removeDecimalIfNotNecessary}}</span>
            </h6>
            <h6 class="text-muted font-weight-bold">
              {{$t($getConst.pages.invoice_details.pay_code)}}:
              <span class="font-weight-light">{{invoice_payments.pay_code}}</span>
            </h6>
            <h6 class="text-muted font-weight-bold">
              {{$t($getConst.pages.invoice_details.net_supplier_payable)}}:
              <span class="font-weight-light">{{invoice_payments.net_supplier_payable}}</span>
            </h6>
            <h6 class="text-muted font-weight-bold">
              {{$t($getConst.pages.invoice_details.total_tax_amount)}}:
              <span class="font-weight-light">{{invoice_payments.total_tax_amount}}</span>
            </h6>
            <h6 class="text-muted font-weight-bold">
              {{$t($getConst.pages.invoice_details.project_id)}}:
              <span class="font-weight-light">{{invoice_payments.project_id}}</span>
            </h6>
          </b-card>
        </b-col>
      </b-row>
    </AppSection>
  </div>
</template>

<script>
  import AppSection from "../../../components/common/sections/AppSection";
  import {formatDateByScope} from "../../../shared/services/date";

  export default {
    name: "InvoiceDetails",
    components: {AppSection},
    props: {
      invoice: null,
      isLoading: {
        type: Boolean,
        default: false
      }
    },
    methods: {
      formatDate(date) {
        const formatD = formatDateByScope(date, this.$store.getters["auth/scopeDefault"].format_date);
        return formatD;
      },
    }
  }
</script>

<style scoped>
  span {
    margin-left: .5rem;
    float: right;
  }
</style>
